/* eslint-disable react-hooks/exhaustive-deps */
import * as turf from '@turf/turf';
import { Feature, Point } from '@turf/turf';
import L from 'leaflet';
import { throttle } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-leaflet';
import { useBoolFlag, useNumberFlag } from '../../../flags/hooks';
import { ThingEventProperties } from '../../thing-event-pane/thing-event-pane.types';
import { AntPolyline } from './ant-polyline';
import MarkerClusters from './marker-clusters';
import { MouseOverLine } from './mouse-over-line';
import NormalPolyline from './normal-polyline';
import { RoutingItemSnapProps } from './route-matching.types';
import { createOffsetLine, snapPointToLine } from './util';

export const RoutingItemSnap: FC<RoutingItemSnapProps> = ({
  color,
  handleBreadcrumbClick,
  hoveredEventId,
  isAntPolylineEnabled,
  isOngoing,
  isSelected,
  isVisible,
  locations,
  renderer,
  routeData,
  selectedEvent,
  setHoveredEventId,
  setSelectedEvent,
  showLocationUpdates,
  journeyId,
}) => {
  const map = useMap();
  const [offsetLine, setOffsetLine] = useState<turf.Feature<turf.LineString, turf.Properties>>();
  const [snappedGeoLine, setSnappedGeoLine] = useState<turf.Feature<turf.LineString, turf.Properties>>();
  const fallbackGeoLine = useMemo(() => {
    const coords = locations.map(({ geometry }) => [geometry.coordinates[1], geometry.coordinates[0]] as [number, number]);

    return coords.length >= 2 ? turf.lineString(coords) : null;
  }, [locations]);
  const clusterDistance = useNumberFlag('track-history-feature-clustering-distance-snapped') ?? 120;
  const [visibleNearestPoints, setVisibleNearestPoints] = useState<turf.Feature<turf.Point, ThingEventProperties>[]>([]);
  const mapClusteringV2 = useBoolFlag('track-journey-map-cluster-enhancements-temporary');

  useEffect(() => {
    if (!routeData?.response?.route[0]) return;
    const { leg, waypoint } = routeData.response.route[0];

    const legData = leg[0].link.flatMap(({ shape }) => {
      const shapeLength = shape.length;
      const legShape = Array.from({ length: shapeLength / 2 }, (_, i) => {
        return L.latLng(shape[2 * i], shape[2 * i + 1]);
      });
      return shapeLength > 2 ? legShape.slice(0, -1) : legShape;
    });

    if (legData.length < 2) return;

    const startWaypoint = waypoint[0].mappedPosition;
    const endWaypoint = waypoint[waypoint.length - 1].mappedPosition;
    const lineString = turf.lineString([
      [startWaypoint.latitude, startWaypoint.longitude],
      ...legData.map(({ lat, lng }) => [lat, lng]),
      [endWaypoint.latitude, endWaypoint.longitude],
    ]);
    const geoLineString = turf.lineString([
      [startWaypoint.longitude, startWaypoint.latitude],
      ...legData.map(({ lat, lng }) => [lng, lat]),
      [endWaypoint.longitude, endWaypoint.latitude],
    ]);

    setSnappedGeoLine(geoLineString);
    setOffsetLine(createOffsetLine(lineString, 2));
  }, [routeData]);

  const nearestPoints: turf.Feature<turf.Point, ThingEventProperties>[] = useMemo(() => {
    if (!snappedGeoLine) {
      return locations.map(({ properties, geometry: { coordinates } }) => {
        return turf.point([coordinates[0], coordinates[1]], {
          ...properties,
          cluster: false,
        });
      });
    }
    return locations.map((location) => {
      const [lng, lat] = snapPointToLine(snappedGeoLine.geometry.coordinates, location.geometry.coordinates);
      return turf.point([lng, lat], {
        ...location.properties,
        cluster: false,
      });
    });
  }, [locations, snappedGeoLine]);

  const updateVisiblePoints = useCallback(() => {
    if (nearestPoints.length === 0) {
      setVisibleNearestPoints([]);
      return;
    }

    const bounds = map.getBounds();

    if (nearestPoints.length === 1) {
      const point = nearestPoints[0];
      const isVisibleInView = bounds.contains(L.latLng(point.geometry.coordinates[1], point.geometry.coordinates[0]));
      setVisibleNearestPoints(isVisibleInView ? [point] : []);
      return;
    }

    const startPoint: Feature<Point, ThingEventProperties> = turf.point(
      [nearestPoints[0].geometry.coordinates[0], nearestPoints[0].geometry.coordinates[1]],
      nearestPoints[0].properties,
    );

    const endPoint: Feature<Point, ThingEventProperties> = turf.point(
      [nearestPoints[nearestPoints.length - 1].geometry.coordinates[0], nearestPoints[nearestPoints.length - 1].geometry.coordinates[1]],
      nearestPoints[nearestPoints.length - 1].properties,
    );

    const middlePoints: Feature<Point, ThingEventProperties>[] = nearestPoints.slice(1, -1)
      .map((location) => turf.point([location.geometry.coordinates[0], location.geometry.coordinates[1]], location.properties))
      .filter((point) => bounds.contains(L.latLng(point.geometry.coordinates[1], point.geometry.coordinates[0])));

    const visiblePoints: Feature<Point, ThingEventProperties>[] = [startPoint, ...middlePoints, endPoint];

    setVisibleNearestPoints([...visiblePoints]);
  }, [map, nearestPoints]);

  useEffect(() => {
    const updateVisiblePointsThrottled = throttle(updateVisiblePoints, 100);
    updateVisiblePointsThrottled();

    map.on('moveend', updateVisiblePointsThrottled);
    map.on('zoomend', updateVisiblePointsThrottled);

    return () => {
      map.off('moveend', updateVisiblePointsThrottled);
      map.off('zoomend', updateVisiblePointsThrottled);
      updateVisiblePointsThrottled.cancel();
    };
  }, [map, updateVisiblePoints]);

  const lineComponent = useMemo(() => {
    const lineData = offsetLine ?? fallbackGeoLine;
    if (!lineData) return <></>;

    if (isAntPolylineEnabled) {
      return (
        <AntPolyline
          color={color}
          data={lineData}
          isEnabled={isAntPolylineEnabled}
          map={map}
          paused={!isSelected}
        />
      );
    }

    return (
      <NormalPolyline
        color={color}
        data={lineData}
        renderer={renderer}
      />
    );
  }, [offsetLine, fallbackGeoLine, color, isSelected, isAntPolylineEnabled]);

  const interactiveLineData = offsetLine ?? fallbackGeoLine;

  if (!isVisible || !routeData || !nearestPoints.length) return <></>;

  return (
    <>
      <MarkerClusters
        journeyId={journeyId}
        clusterRadius={clusterDistance}
        handleBreadcrumbClick={handleBreadcrumbClick}
        hoveredEventId={hoveredEventId}
        isSelected={isSelected}
        isOngoing={isOngoing}
        isVisible={isVisible}
        nearestPoints={nearestPoints}
        setHoveredEventId={setHoveredEventId}
        setSelectedEvent={setSelectedEvent}
        showLocationUpdates={showLocationUpdates}
      />
      {lineComponent}
      {!mapClusteringV2 && interactiveLineData && <MouseOverLine
        clusterRadius={clusterDistance}
        data={interactiveLineData}
        handleBreadcrumbClick={handleBreadcrumbClick}
        nearestPoints={visibleNearestPoints}
        renderer={renderer}
        selectedEvent={selectedEvent}
        setHoveredEventId={setHoveredEventId}
        setSelectedEvent={setSelectedEvent}
      />
      }
    </>
  );
};
